/** The one place where numbers typed by a person are read and shown (owner rule: a comma and a dot are interchangeable, we display a comma). */

export type DecimalOptions = {
  /** Most digits after the separator; 0 (the default) means a whole number. */
  maxFractionDigits?: number;
  /** Allow a leading minus (also "−"). Off by default. */
  negative?: boolean;
  /** Most digits before the separator. */
  maxIntegerDigits?: number;
};

type Parts = { negative: boolean; int: string; frac: string };

function split(text: string, opts: DecimalOptions): Parts | null {
  const t = text.replace(/[\s  ]/g, '');
  const m = /^([+\-−])?(\d*)(?:[.,](\d*))?$/.exec(t);
  if (!m) return null;
  const int = m[2] ?? '';
  const frac = m[3] ?? '';
  if (int === '' && frac === '') return null;
  const negative = m[1] === '-' || m[1] === '−';
  if (negative && !opts.negative) return null;
  if (frac.length > (opts.maxFractionDigits ?? 0)) return null;
  if (int.length > (opts.maxIntegerDigits ?? 15)) return null;
  return { negative, int: int === '' ? '0' : int, frac };
}

/** "1 234,5" / "1234.5" / "+3" -> number; null for empty text or anything else. */
export function parseDecimal(text: string, opts: DecimalOptions = {}): number | null {
  const p = split(text, opts);
  return p ? Number(`${p.negative ? '-' : ''}${p.int}.${p.frac || '0'}`) : null;
}

/** Like parseDecimal, but returns an exact integer multiplied by 10^scale (grosze: 2, basis points: 2, thousandths: 3); allows `scale` fraction digits. */
export function parseScaled(text: string, scale: number, opts: Omit<DecimalOptions, 'maxFractionDigits'> = {}): number | null {
  const p = split(text, { ...opts, maxFractionDigits: scale });
  if (!p) return null;
  const n = Number(p.int) * 10 ** scale + Number(p.frac.padEnd(scale, '0') || '0');
  return p.negative ? -n : n;
}

/** 12.5 -> "12,5"; `digits` pads to a fixed number of decimals, `group` separates thousands with a non-breaking space. */
export function formatDecimal(n: number, opts: { digits?: number; group?: boolean } = {}): string {
  const fixed = opts.digits === undefined ? String(n) : n.toFixed(opts.digits);
  const [whole = '', frac] = fixed.split('.');
  const grouped = opts.group ? whole.replace(/\B(?=(\d{3})+$)/g, ' ') : whole;
  return frac ? `${grouped},${frac}` : grouped;
}

/** While typing: every dot (also the one the numeric keypad sends) becomes a comma. */
export const normalizeTyping = (text: string): string => text.replace(/\./g, ',');
